import { useState, useEffect } from 'react';
import { Box, Divider, Grid, Stack, Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import { selectLoggedInUserEmail } from '@store/auth';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import FormSaveAndSubmit from '@components/FormSaveAndSubmit';
import ConfirmationDialog from '@components/ConfirmationDialog';
import DetailsForm from './components/DetailsForm';
import ConditionsForm from './components/ConditionsForm';
import OutputForm from './components/OutputForm';

import {
  createDynamicRule,
  updateDynamicRule,
  deleteDynamicRule,
  getSelectedDynamicRuleDetails,
  selectIsCreatingDynamicRule,
  selectIsUpdatingDynamicRule,
  paymentOrchestrationRulesActions,
  selectIsLoadingSelectedDynamicRuleDetails,
  selectSelectedDynamicRuleDetails,
} from '../../../store/payment-orchestration-dynamic-rules.slice';

import {
  dynamicRuleAddBreadcrumb,
  DYNAMIC_RULE_LIST_ROUTE,
  updateDynamicRuleBreadcrumb,
} from '../../../payment-orchestration.breadcrumbs';
import { DYNAMIC_RULES_EFFECTIVE_DATE_FORMAT } from '../../../payment-orchestration.constants';
import {
  getCreateDynamicRulePayload,
  getDynamicRuleDetailsFormValues,
  createEmptyDynamicRuleCondition,
  createEmptyDynamicRuleOutput,
} from '../../../payment-orchestration.utils';

const INITIAL_FORM = {
  details: {
    type: '',
    subType: '',
    effectiveStartDate: dayjs(Date.now()).format(
      DYNAMIC_RULES_EFFECTIVE_DATE_FORMAT,
    ),
    effectiveEndDate: dayjs(Date.now())
      .add(1, 'day')
      .format(DYNAMIC_RULES_EFFECTIVE_DATE_FORMAT),
  },
  conditions: [createEmptyDynamicRuleCondition()],
  output: {
    isJsonView: false,
    keyValue: [createEmptyDynamicRuleOutput()],
    json: '',
  },
};

const MESSAGES = {
  CreateSuccess: 'Yay! A dynamic rule has been created successfully.',
  CreateFailure: 'Oops! Something went wrong while creating the dynamic rule.',
  UpdateSuccess: 'Yay! Dynamic rule has been updated successfully.',
  UpdateFailure: 'Oops! Something went wrong while updating the dynamic rule.',
  DeleteSuccess: 'Yay! Dynamic rule has been deleted successfully.',
  DeleteFailure: 'Oops! Something went wrong while deleting the dynamic rule.',
  FormRequiredFieldError: 'This is a required field',
  FormEffectivityStartDateFieldError: 'Start date should be today or later',
  FormEffectivityEndDateFieldError:
    'End date should be greater than start date',
};

const FORM_VALIDATORS = {
  required: (value) => (value ? undefined : MESSAGES.FormRequiredFieldError),
  restrictPastDate: (value) =>
    dayjs(value).isBefore(dayjs().add(-1, 'day'))
      ? MESSAGES.FormEffectivityStartDateFieldError
      : undefined,
  effectivityEndDateGreaterThanStartDate: (
    value,
    { details: { effectiveStartDate } },
  ) =>
    dayjs(value).isAfter(effectiveStartDate)
      ? undefined
      : MESSAGES.FormEffectivityEndDateFieldError,
};

export default function DynamicRulesForm({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const userEmail = useSelector(selectLoggedInUserEmail);
  const isCreatingDynamicRule = useSelector(selectIsCreatingDynamicRule);
  const isUpdatingDynamicRule = useSelector(selectIsUpdatingDynamicRule);
  const isLoadingSelectedDynamicRuleDetails = useSelector(
    selectIsLoadingSelectedDynamicRuleDetails,
  );
  const selectedDynamicRuleDetails = useSelector(
    selectSelectedDynamicRuleDetails,
  );
  const [openDialog, setOpenDialog] = useState({ show: false });

  const [formValue, setFormValue] = useState(INITIAL_FORM);
  const [isEditMode, setIsEditMode] = useState(id !== undefined);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState({
    show: false,
  });

  useEffect(() => {
    if (!permissions?.canCreatePaymentOrchDynamicRule) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    setIsEditMode(id !== undefined);
  }, [id]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu());

    // CREATE mode
    if (!isEditMode) {
      dispatch(breadcrumbsActions.setBreadcrumbs(dynamicRuleAddBreadcrumb));
      dispatch(paymentOrchestrationRulesActions.resetSelectedDynamicRule());
      return;
    }

    // EDIT mode
    dispatch(
      breadcrumbsActions.setBreadcrumbs(updateDynamicRuleBreadcrumb(id)),
    );
    dispatch(getSelectedDynamicRuleDetails(id));
  }, [id, isEditMode, dispatch]);

  useEffect(() => {
    if (!isEditMode) {
      setFormValue(INITIAL_FORM);
      return;
    }

    if (!selectedDynamicRuleDetails) return;

    const formattedFormValues = getDynamicRuleDetailsFormValues(
      selectedDynamicRuleDetails,
    );
    setFormValue(formattedFormValues);
  }, [id, isEditMode, selectedDynamicRuleDetails]);

  const onSubmit = (values) => {
    const payload = getCreateDynamicRulePayload(values, userEmail);

    let action = createDynamicRule(payload);
    let successMsg = MESSAGES.CreateSuccess;
    let failureMsg = MESSAGES.CreateFailure;

    if (isEditMode) {
      action = updateDynamicRule({ id, ...payload });
      successMsg = MESSAGES.UpdateSuccess;
      failureMsg = MESSAGES.UpdateFailure;
    }

    dispatch(action).then(({ payload: response }) => {
      // On Failure
      if (!response || response.status !== 'SUCCESS') {
        const message = response?.errorMessage || failureMsg;
        dispatch(showErrorMessage({ message, duration: 5000 }));

        return;
      }

      // On Success
      dispatch(showSuccessMessage(successMsg));
      navigate(DYNAMIC_RULE_LIST_ROUTE);
    });
  };

  const onDeleteRule = () => {
    dispatch(deleteDynamicRule({ id, userEmail })).then(
      ({ payload: response }) => {
        // On Failure
        if (!response || response.status !== 'SUCCESS') {
          const message = response?.errorMessage || MESSAGES.DeleteFailure;
          dispatch(showErrorMessage({ message, duration: 5000 }));

          return;
        }

        // On Success
        dispatch(showSuccessMessage(MESSAGES.DeleteSuccess));
        navigate(DYNAMIC_RULE_LIST_ROUTE);
      },
    );
  };

  const renderFormActions = ({
    pristine,
    submitting,
    invalid,
    form,
    permissions: userPermissions,
  }) => (
    <Grid
      item
      xs={12}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 3,
      }}>
      {isEditMode && (
        <>
          <Button
            variant="contained"
            disabled={!userPermissions.canDeletePaymentOrchDynamicRule}
            onClick={() => {
              setShowDeleteConfirmation({ show: true });
            }}
            startIcon={<DeleteIcon />}>
            Delete
          </Button>
          <Divider
            orientation="vertical"
            flexItem
          />
        </>
      )}
      <Box>
        <FormSaveAndSubmit
          id={id}
          submitLoading={isCreatingDynamicRule || isUpdatingDynamicRule}
          resetDisabled={pristine}
          submitDisabled={
            !userPermissions.canCreatePaymentOrchDynamicRule ||
            !userPermissions.canUpdatePaymentOrchDynamicRule ||
            submitting ||
            pristine ||
            invalid
          }
          handleReset={() => {
            form.reset();
          }}
        />
      </Box>
    </Grid>
  );

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate pageReady={!isLoadingSelectedDynamicRuleDetails}>
      <Form
        onSubmit={onSubmit}
        initialValues={{ ...formValue }}
        mutators={{
          ...arrayMutators,
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
        }}
        render={({
          handleSubmit,
          values,
          pristine,
          submitting,
          invalid,
          form,
          form: {
            mutators: { push, setValue },
          },
        }) => (
          <form
            onSubmit={(e) =>
              process.env.REACT_APP_ENV !== 'production'
                ? handleSubmit(e)
                : handlePreSubmit(e)
            }
            noValidate>
            <ProdConfirmationDialog
              openDialog={openDialog}
              yesHandler={handleSubmit}
            />
            <Grid
              container
              spacing={1}
              mb={2}>
              {renderFormActions({
                form,
                submitting,
                pristine,
                invalid,
                values,
                permissions,
              })}
            </Grid>
            <Stack
              gap={4}
              mb={4}>
              <DetailsForm
                isEditMode={isEditMode}
                values={values}
                validators={FORM_VALIDATORS}
                setValue={setValue}
              />
              <ConditionsForm
                values={values}
                validators={FORM_VALIDATORS}
                push={push}
              />
              <OutputForm
                values={values}
                validators={FORM_VALIDATORS}
                push={push}
                setValue={setValue}
              />
            </Stack>
          </form>
        )}
      />
      <ConfirmationDialog
        openDialog={showDeleteConfirmation}
        yesHandler={onDeleteRule}
        title="Delete"
        message="Are you sure you want to delete the this rule?"
      />
    </ApplicationPageTemplate>
  );
}
